import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { getErrorMessage } from "@luce/core/apollo";

import { useClientId, useClientProfile, useCountdown } from "../../shared";
import {
  isPinCodeStillActive,
  OTP_CODE_LIFETIME_SECONDS,
  withLeadingPlus,
} from "../account-info/lib";
import { useClientConfirmPhoneNumber } from "../gqlhooks/use-client-confirm-phone-number";
import { useClientVerifyPhoneNumber } from "../gqlhooks/use-client-verify-phone-number";
import { useUpdateAccountPhoneNumber } from "../gqlhooks/use-update-account-phone-number";
import { changePhoneNumberSchema, SG_DIAL_CODE } from "./lib";
import type { ChangePhoneNumberFormValues, ChangePhoneNumberStep } from "./types";

interface UseChangePhoneNumberParams {
  onExit: () => void;
  onPhoneNumberChanged?: () => void;
}

export function useChangePhoneNumber({ onExit, onPhoneNumberChanged }: UseChangePhoneNumberParams) {
  const clientId = useClientId();
  const { client } = useClientProfile();
  const phoneNumber = client?.user?.phoneNumber;
  const currentPhoneNumber = phoneNumber ? withLeadingPlus(phoneNumber) : "";
  const isSupportedNumber = currentPhoneNumber.startsWith(SG_DIAL_CODE);

  const [step, setStep] = useState<ChangePhoneNumberStep>("form");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const { remaining, restart } = useCountdown(OTP_CODE_LIFETIME_SECONDS);

  const [verifyPhoneNumber, { loading: isSendingOtp }] = useClientVerifyPhoneNumber();
  const [confirmPhoneNumber, { loading: isConfirmingOtp }] = useClientConfirmPhoneNumber();
  const [updateAccountPhoneNumber, { loading: isUpdating }] = useUpdateAccountPhoneNumber();

  const form = useForm<ChangePhoneNumberFormValues>({
    mode: "onChange",
    resolver: zodResolver(changePhoneNumberSchema),
    values: { currentPhoneNumber, newPhoneNumber: SG_DIAL_CODE },
    resetOptions: { keepDirtyValues: true },
  });

  const openOtp = (phone: string) => {
    setNewPhoneNumber(phone);
    setPinCode("");
    setOtpError("");
    restart();
    setStep("otp");
  };

  const onSubmit = form.handleSubmit(async (values) => {
    const phone = withLeadingPlus(values.newPhoneNumber);
    try {
      await verifyPhoneNumber(phone, true);
      openOtp(phone);
    } catch (error) {
      const message = getErrorMessage(error, "Failed to send verification code");
      if (isPinCodeStillActive(message)) {
        openOtp(phone);
        return;
      }
      form.setError("newPhoneNumber", { message });
    }
  });

  const confirmOtp = async () => {
    setOtpError("");
    try {
      const confirmed = await confirmPhoneNumber(newPhoneNumber, pinCode, true);
      if (!confirmed) {
        setOtpError("Invalid verification code");
        return;
      }
      const updated = await updateAccountPhoneNumber(clientId, newPhoneNumber);
      if (!updated) {
        setOtpError("Failed to update phone number");
        return;
      }
      onPhoneNumberChanged?.();
      setStep("success");
    } catch (error) {
      setOtpError(getErrorMessage(error, "Failed to update phone number"));
    }
  };

  return {
    step,
    form,
    isSupportedNumber,
    newPhoneNumber,
    pinCode,
    otpError,
    remaining,
    isSendingOtp,
    isConfirmingOtp: isConfirmingOtp || isUpdating,
    setPinCode,
    onSubmit,
    confirmOtp,
    requestCancel: () => setStep("cancel"),
    resumeOtp: () => setStep("otp"),
    exit: onExit,
  };
}
